import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_TABLES_PER_SESSION, STATE_ORDER, deriveTableState } from './tableState';
import { sortWaiterTables } from './tableSort';
import {
  blockReason,
  groupUuids,
  isGroupChip,
  mergeBlockReason,
  pickerRows,
  regroupResult,
  togglePick,
  type BlockReason,
  type MergeBlockReason,
  type RegroupMode,
} from './regroupSelection';
import type { WaiterArea, WaiterTable } from './waiterApi';

interface Props {
  mode: RegroupMode;
  /** The tapped anchor row (never a member row — the member sheet only separates). */
  source: WaiterTable;
  tables: WaiterTable[];
  areas: WaiterArea[];
  picked: string[];
  onPick: (next: string[]) => void;
}

const BLOCK_LABEL: Record<BlockReason | MergeBlockReason, string> = {
  payment: 'waiter.overview.regroupBlockedPayment',
  caisse: 'waiter.overview.regroupBlockedCaisse',
  pending: 'waiter.overview.regroupBlockedPending',
  buffet: 'waiter.overview.regroupBlockedBuffet',
};

interface Section {
  key: string;
  name: string;
  color: string | null;
  tables: WaiterTable[];
}

/**
 * SC-976 regroup (2026-09-16) — the destination grid of the ⋮ sheet's
 * « Transférer » / « Réunir » tabs. Every table of the restaurant, grouped by
 * salle (its colour on the heading), each chip painted with the same state
 * colours as the salle grid (`.w-tile-<state>`), so the waiter recognises the
 * room at a glance. A merged note is ONE chip labelled « 9+10 », in its
 * anchor's salle, and a tap picks the whole group (`pickerRows`).
 */
export default function RegroupPicker({ mode, source, tables, areas, picked, onPick }: Props) {
  const { t } = useTranslation();
  const sessionUuid = source.session?.uuid ?? '';
  const tablesByUuid = useMemo(() => new Map(tables.map((tb) => [tb.uuid, tb])), [tables]);
  const result = regroupResult(mode, source, picked, tablesByUuid);

  const sections = useMemo<Section[]>(() => {
    const known = new Set(areas.map((a) => a.uuid));
    const sorted = pickerRows(mode, sortWaiterTables(tables, 'room'), sessionUuid);
    const out: Section[] = areas.map((a) => ({
      key: a.uuid,
      name: a.name,
      color: a.color,
      tables: sorted.filter((tb) => tb.area?.uuid === a.uuid),
    }));
    out.push({
      key: 'none',
      name: areas.length > 0 ? t('waiter.overview.regroupNoArea') : '',
      color: null,
      tables: sorted.filter((tb) => !tb.area || !known.has(tb.area.uuid)),
    });
    return out.filter((s) => s.tables.length > 0);
  }, [areas, tables, mode, sessionUuid, t]);

  return (
    // `flex-auto min-h-0` down to the grid: the grid is the sheet's only
    // scroller, the buttons under it never shrink (390/360px review).
    <div className="flex flex-col gap-2 flex-auto min-h-0">
      <p className="shrink-0 font-sans text-base text-ink-mute">
        {mode === 'move'
          ? t('waiter.overview.regroupMovePickLabel', { max: MAX_TABLES_PER_SESSION })
          : t('waiter.overview.regroupMergePickLabel')}
      </p>
      <div className="shrink-0 flex flex-wrap items-center gap-x-3 gap-y-1">
        {STATE_ORDER.map((state) => (
          <span
            key={state}
            className={`w-chip-${state} flex items-center gap-1 font-sans text-[13px] font-semibold text-ink`}
          >
            {t(`waiter.notes.states.${state}`)}
          </span>
        ))}
        <span
          data-testid="regroup-count"
          className="ms-auto font-mono text-[14px] font-bold text-ink"
          aria-live="polite"
        >
          {t('waiter.overview.regroupCount', { n: result.length, max: MAX_TABLES_PER_SESSION })}
        </span>
      </div>
      {/* pe-2 here and pt-2 on each chip grid: room for a selected chip's 3px
          outline + offset and its corner badge, which the scroller would
          otherwise clip. No top padding on the scroller itself — the sticky
          salle heading would stop 8px under its edge and let the chips
          scroll past in that gap. */}
      <div
        data-testid="regroup-grid"
        className="flex flex-col gap-3 flex-auto min-h-[120px] overflow-y-auto overflow-x-hidden pe-2"
      >
        {sections.map((section) => (
          <section key={section.key} data-testid={`regroup-area-${section.key}`} className="flex flex-col gap-1.5">
            {section.name && (
              <h4 className="sticky top-0 z-10 flex items-center gap-2 bg-paper py-1 font-sans text-[15px] font-bold text-ink shadow-[0_2px_0_var(--paper)]">
                <span
                  aria-hidden="true"
                  data-testid={`regroup-area-color-${section.key}`}
                  className="inline-block w-3.5 h-3.5 rounded-full border border-hairline shrink-0"
                  style={{ backgroundColor: section.color ?? 'transparent' }}
                />
                {section.name}
              </h4>
            )}
            <div
              className="grid grid-cols-[repeat(auto-fill,minmax(76px,1fr))] gap-2 ps-2 pt-2 border-s-4"
              style={{ borderColor: section.color ?? 'var(--hairline, #e5e7eb)' }}
            >
              {section.tables.map((table) => {
                const own = table.session?.uuid === sessionUuid && sessionUuid !== '';
                const blocked = blockReason(table, source);
                // SC-1007 — a note that can't be merged in has no other way in
                // (« Écraser » is gone), so it is not pickable either.
                const hint = blocked ?? mergeBlockReason(table, source);
                const selected = own && mode === 'merge' ? true : picked.includes(table.uuid);
                const position = selected && mode === 'move' ? result.indexOf(table.uuid) + 1 : 0;
                const lockedOwn = own && mode === 'merge';
                const wouldOverflow = !selected && togglePick(mode, source, picked, table, tablesByUuid) === null;
                const disabled = lockedOwn || hint !== null || wouldOverflow;
                const group = isGroupChip(mode, table, sessionUuid);
                const size = group ? groupUuids(table).length : 1;
                const name = group ? (table.session?.tableLabel ?? String(table.number)) : String(table.number);
                const sub = own ? t('waiter.overview.regroupCurrent') : hint ? t(BLOCK_LABEL[hint]) : null;
                return (
                  <button
                    key={table.uuid}
                    type="button"
                    data-testid={`transfer-dest-${table.uuid}`}
                    data-state={deriveTableState(table)}
                    data-group-size={group ? size : undefined}
                    aria-label={[t('waiter.ticket.header.tableLabel', { table: name }), sub]
                      .filter(Boolean)
                      .join(' · ')}
                    aria-pressed={selected}
                    disabled={disabled}
                    onClick={() => {
                      const next = togglePick(mode, source, picked, table, tablesByUuid);
                      if (next) onPick(next);
                    }}
                    className={`w-tile-${deriveTableState(table)} relative min-h-14 ${size >= 3 ? 'col-span-2' : ''} rounded-btn border-solid px-1 py-1 flex flex-col items-center justify-center font-sans leading-tight ${
                      selected ? 'outline outline-[3px] outline-offset-2 outline-ink' : ''
                    } ${
                      // A blocked chip keeps its text at full contrast (its
                      // reason must stay readable) and says so with a dashed
                      // ring; a chip that only overflows the cap is faded.
                      blocked !== null ? 'border-dashed' : disabled && !lockedOwn ? 'opacity-75' : ''
                    }`}
                  >
                    {group ? (
                      <span className="flex items-baseline gap-1 max-w-full">
                        <span aria-hidden="true" className="text-[13px]">
                          ⛓
                        </span>
                        <bdi dir="ltr" className="text-[18px] font-bold break-all text-center">
                          {name}
                        </bdi>
                      </span>
                    ) : (
                      <bdi dir="ltr" className="text-[20px] font-bold max-w-full truncate">
                        {table.number}
                      </bdi>
                    )}
                    {sub && (
                      <span className="text-[11px] font-semibold max-w-full text-center break-words line-clamp-2">
                        {sub}
                      </span>
                    )}
                    {selected && (
                      <span
                        aria-hidden="true"
                        className="absolute -top-2 -end-2 min-w-5 h-5 px-1 rounded-full bg-ink text-paper text-[12px] font-bold flex items-center justify-center"
                      >
                        {position > 0 ? position : '✓'}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
